import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';

const LOGIN_BG = 'https://static.prod-images.emergentagent.com/jobs/492c48d5-8c0e-475c-834a-63b6e62513aa/images/a2966ef9aa3a3462b8d038b792a36baa27d8b417a706bff7330b944cda86c43b.png';
const HIMT_LOGO = 'https://customer-assets.emergentagent.com/job_report-approval-flow/artifacts/034nkv97_HIMT%20LOGO-PNG.png';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-10">
            <img src={HIMT_LOGO} alt="HIMT" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                HIMT
              </h1>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Weekly Report System
              </p>
            </div>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome back
          </h2>
          <p className="text-slate-500 mb-8 text-sm font-medium">
            Sign in to access the staff activity reporting system
          </p>

          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@himt.edu"
                required
                className="border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <div>
              <Label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 font-medium py-2.5 rounded-md transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="Maritime" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <h3 className="text-white text-3xl font-black tracking-tighter mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Hindustan Institute of Maritime Training
          </h3>
          <p className="text-white/80 text-sm leading-relaxed max-w-md">
            Digital staff activity reporting system. Submit, review, and track weekly reports with multi-level approval workflow.
          </p>
        </div>
      </div>
    </div>
  );
}
